// @flow strict
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';


export const QuestionTypeAndAttributeNameMapping: Array<{
  attributeName: string,
  questionType: string,
}> = [
  {
    questionType: 'nps_survey_question',
    attributeName: 'survey_nps_score',
  },
  {
    questionType: 'calendar_date_survey_question',
    attributeName: 'survey_calendar_date',
  },
  {
    questionType: 'rating_scale_survey_question',
    attributeName: 'survey_rating_scale',
  },
  {
    questionType: 'text_survey_question',
    attributeName: 'survey_text',
  },
  {
    questionType: 'multiple_choice_survey_question',
    attributeName: 'survey_multiple_options',
  },
];

export function getQuestionTypeFromAttributeName(
  attributeName: string,
): string {
  const mapping = QuestionTypeAndAttributeNameMapping.find(
    (item) => item.attributeName === attributeName,
  );
  return mapping ? mapping.questionType : '';
}

export function getAttributeNameFromQuestionType(questionType: string): string {
  const mapping = QuestionTypeAndAttributeNameMapping.find(
    (item) => item.questionType === questionType,
  );
  return mapping ? mapping.attributeName : '';
}

export const getNpsOperatorOptions = (
  t: (string, string) => string,
): {value: string, label: string}[] => [
  {value: 'eq', label: t('is_equal_to', 'is equal to')},
  {value: 'gt', label: t('is_greater_than', 'is greater than')},
  {value: 'lt', label: t('is_less_than', 'is less than')},
  {
    value: 'gte',
    label: t('is_greater_than_or_equal_to', 'is greater than or equal to'),
  },
  {
    value: 'lte',
    label: t('is_less_than_or_equal_to', 'is less than or equal to'),
  },
];

export const getCalendarDateOperatorOptions = (
  t: (string, string) => string,
): {value: string, label: string}[] => [
  {
    value: 'gt',
    label: t('is_after', 'is after'),
  },
  {
    value: 'lt',
    label: t('is_before', 'is before'),
  },
  {
    value: 'eq',
    label: t('is_equal_to', 'is equal to'),
  },
  {
    value: 'gte',
    label: t('is_on_or_after', 'is on or after'),
  },
  {
    value: 'lte',
    label: t('is_on_or_before', 'is on or before'),
  },
];

export const moduleTypeLabelMap = (
  t: (string, string) => string,
): {
  [key: string]: {
    label: string,
    defaultOperator: string,
    operatorOptions: {value: string, label: string}[],
  },
} => {
  const NPS_OPERATOR_OPTIONS = getNpsOperatorOptions(t);
  const CALENDAR_DATE_OPERATOR_OPTIONS = getCalendarDateOperatorOptions(t);
  return {
    nps_survey_question: {
      label: t('NPS', 'NPS'),
      defaultOperator: 'gt',
      operatorOptions: NPS_OPERATOR_OPTIONS,
    },
    rating_scale_survey_question: {
      label: t('Rating', 'Rating'),
      defaultOperator: 'gt',
      operatorOptions: NPS_OPERATOR_OPTIONS,
    },
    text_survey_question: {
      label: t('Free_Text', 'Free Text'),
      defaultOperator: 'contains',
      operatorOptions: [{value: 'contains', label: t('contains', 'contains')}],
    },
    multiple_choice_survey_question: {
      label: t('MCQ', 'MCQ'),
      defaultOperator: 'in',
      operatorOptions: [{value: 'in', label: t('is_one_of', 'is one of')}],
    },
    calendar_date_survey_question: {
      label: t('Date', 'Date'),
      defaultOperator: 'lte',
      operatorOptions: CALENDAR_DATE_OPERATOR_OPTIONS,
    },
    default: {
      label: t('Default', 'Default'),
      defaultOperator: 'eq',
      operatorOptions: [{value: 'eq', label: t('is_equal_to', 'is equal to')}],
    },
  };
};

export const getNpsValues = (
  minRating: number,
  maxRating: number,
): MenuOption[] => {
  const valueArray = [];
  for (let i = minRating; i <= maxRating; i++) {
    valueArray.push({key: String(i), label: String(i)});
  }
  return valueArray;
};

export const getRatingScaleValues = (): {label: string, value: string}[] => {
  const valueArray = [];
  for (let i = 1; i <= 10; i++) {
    valueArray.push({label: String(i), value: String(i)});
  }
  return valueArray;
};
